import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FiInstagram, FiFacebook, FiTwitter, FiBox, FiCalendar, FiShield } from 'react-icons/fi';
import { getVendor, getProducts } from '../services/api';
import ProductCard from '../components/product/ProductCard';
import StarRating from '../components/common/StarRating';
import PageTransition from '../components/common/PageTransition';
import './VendorStorePage.css';

const picsum = (seed) => `https://picsum.photos/seed/${seed}/900/280`;


const TABS = ['Products', 'About', 'Reviews'];

export default function VendorStorePage() {
  const { id } = useParams();
  const [vendor, setVendor] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [prodLoading, setProdLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('Products');
  const [imgError, setImgError] = useState(false);

  const fetchVendor = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getVendor(id);
      setVendor(data.vendor);
    } catch (err) {
      console.error('Vendor store error:', err?.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchProducts = useCallback(async () => {
    setProdLoading(true);
    try {
      const { data } = await getProducts({ vendor: id, limit: 40, status: 'active' });
      setProducts(data.products || []);
    } catch (err) {
      console.error('Vendor store products error:', err?.response?.data || err.message);
      setProducts([]);
    } finally {
      setProdLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchVendor();
    fetchProducts();
  }, [fetchVendor, fetchProducts]);

  if (loading) {
    return (
      <div className="loading-center" style={{ minHeight: '60vh' }}>
        <div className="spinner" /><span>Loading store…</span>
      </div>
    );
  }

  const bannerSrc = !imgError && vendor?.banner ? vendor.banner : picsum(vendor?._id || id);
  const logoInitial = vendor?.storeName?.[0]?.toUpperCase() || 'V';
  const memberSince = vendor?.createdAt
    ? new Date(vendor.createdAt).toLocaleDateString('en-PK', { month: 'long', year: 'numeric' })
    : '—';

  return (
    <PageTransition>
    <div className="vstore-page">
      {/* Banner */}
      <div className="vstore-banner-wrap">
        <img
          src={bannerSrc}
          alt={vendor?.storeName}
          className="vstore-banner-img"
          onError={() => setImgError(true)}
        />
        <div className="vstore-banner-overlay" />
      </div>

      {/* Store identity */}
      <div className="container vstore-identity-row">
        <div className="vstore-logo-wrap">
          <div className="vstore-logo">
            {vendor?.logo
              ? <img src={vendor.logo} alt={vendor.storeName} />
              : <span>{logoInitial}</span>}
          </div>
        </div>
        <div className="vstore-identity-info">
          <h1 className="vstore-name">{vendor?.storeName}</h1>
          <div className="vstore-meta">
            {vendor?.category && (
              <span className="vstore-meta-item">
                <FiBox size={13} /> {vendor.category}
              </span>
            )}
            <span className="vstore-meta-item">
              <FiCalendar size={13} /> Since {memberSince}
            </span>
            <span className="vstore-meta-item">
              <FiBox size={13} /> {vendor?.totalProducts ?? products.length} products
            </span>
          </div>
          {(vendor?.rating > 0) && (
            <div style={{ marginTop: '0.375rem' }}>
              <StarRating rating={vendor.rating} totalReviews={vendor.totalReviews} size={14} />
            </div>
          )}
        </div>
        <Link to="/vendor/apply" className="btn btn-ghost btn-sm vstore-sell-btn">
          <FiShield size={13} /> Sell Here
        </Link>
      </div>

      {/* Tabs */}
      <div className="container">
        <div className="vstore-tabs">
          {TABS.map((tab) => (
            <button
              key={tab}
              className={`vstore-tab ${activeTab === tab ? 'active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
              {tab === 'Products' && !prodLoading && (
                <span className="vstore-tab-count">{products.length}</span>
              )}
              {tab === 'Reviews' && vendor?.totalReviews > 0 && (
                <span className="vstore-tab-count">{vendor.totalReviews}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div className="container vstore-content">
        {/* Products tab */}
        {activeTab === 'Products' && (
          prodLoading ? (
            <div className="loading-center" style={{ padding: '3rem' }}>
              <div className="spinner" /><span>Loading products…</span>
            </div>
          ) : products.length === 0 ? (
            <div className="empty-state" style={{ padding: '3rem' }}>
              <div className="empty-state-icon">📦</div>
              <h3>No products listed yet</h3>
            </div>
          ) : (
            <div className="vstore-products-grid">
              {products.map((p) => (
                <ProductCard key={p._id} product={p} />
              ))}
            </div>
          )
        )}

        {/* About tab */}
        {activeTab === 'About' && (
          <div className="vstore-about">
            <div className="vstore-about-grid">
              <div className="vstore-about-main">
                <section className="vstore-about-section card">
                  <h2 className="vstore-about-title">About the Store</h2>
                  <p className="vstore-about-desc">
                    {vendor?.description || 'No description provided.'}
                  </p>
                </section>

                {/* Social links */}
                {(vendor?.socialLinks?.instagram || vendor?.socialLinks?.facebook || vendor?.socialLinks?.twitter) && (
                  <section className="vstore-about-section card">
                    <h2 className="vstore-about-title">Follow Us</h2>
                    <div className="vstore-social-links">
                      {vendor.socialLinks.instagram && (
                        <a href={vendor.socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="vstore-social-btn">
                          <FiInstagram size={16} /> Instagram
                        </a>
                      )}
                      {vendor.socialLinks.facebook && (
                        <a href={vendor.socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="vstore-social-btn">
                          <FiFacebook size={16} /> Facebook
                        </a>
                      )}
                      {vendor.socialLinks.twitter && (
                        <a href={vendor.socialLinks.twitter} target="_blank" rel="noopener noreferrer" className="vstore-social-btn">
                          <FiTwitter size={16} /> Twitter / X
                        </a>
                      )}
                    </div>
                  </section>
                )}
              </div>

              <div className="vstore-about-side">
                <section className="vstore-about-section card">
                  <h2 className="vstore-about-title">Store Policies</h2>
                  <ul className="vstore-policies">
                    <PolicyItem icon="🔄" label="Returns" value="7-day hassle-free returns" />
                    <PolicyItem icon="🚚" label="Shipping" value="Free on orders over Rs. 500" />
                    <PolicyItem icon="✅" label="Warranty" value="Manufacturer warranty on all products" />
                    <PolicyItem icon="🔒" label="Secure" value="100% secure transactions" />
                    <PolicyItem icon="💬" label="Support" value="24/7 chat and email support" />
                  </ul>
                </section>

                <section className="vstore-about-section card">
                  <h2 className="vstore-about-title">Store Stats</h2>
                  <ul className="vstore-policies">
                    <PolicyItem icon="📦" label="Products" value={`${vendor?.totalProducts ?? products.length} listed`} />
                    <PolicyItem icon="⭐" label="Rating" value={vendor?.rating ? `${Number(vendor.rating).toFixed(1)} / 5.0` : 'Not yet rated'} />
                    <PolicyItem icon="💬" label="Reviews" value={`${vendor?.totalReviews ?? 0} reviews`} />
                    <PolicyItem icon="📅" label="Member Since" value={memberSince} />
                  </ul>
                </section>
              </div>
            </div>
          </div>
        )}

        {/* Reviews tab */}
        {activeTab === 'Reviews' && (
          <div className="vstore-reviews">
            <div className="vstore-reviews-header">
              <div className="vstore-reviews-score">
                <span className="vstore-score-num">{Number(vendor?.rating || 4.8).toFixed(1)}</span>
                <div>
                  <StarRating rating={vendor?.rating || 4.8} size={16} showCount={false} />
                  <p className="vstore-score-sub">{vendor?.totalReviews ?? 0} reviews</p>
                </div>
              </div>
            </div>
            <div className="vstore-reviews-list">
              <div className="empty-state" style={{ padding: '2rem' }}>
                <div className="empty-state-icon">⭐</div>
                <h3>No reviews yet</h3>
                <p>Be the first to review this store.</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </PageTransition>
  );
}

function PolicyItem({ icon, label, value }) {
  return (
    <li className="vstore-policy-item">
      <span className="vstore-policy-icon">{icon}</span>
      <div>
        <span className="vstore-policy-label">{label}</span>
        <span className="vstore-policy-value">{value}</span>
      </div>
    </li>
  );
}

function ReviewCard({ review }) {
  const date = new Date(review.createdAt).toLocaleDateString('en-PK', { day: 'numeric', month: 'short', year: 'numeric' });
  const initials = review.user?.name?.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div className="vstore-review-card card">
      <div className="vstore-review-header">
        <div className="vstore-reviewer-avatar">{initials}</div>
        <div>
          <p className="vstore-reviewer-name">{review.user?.name}</p>
          <StarRating rating={review.rating} size={12} showCount={false} />
        </div>
        <span className="vstore-review-date">{date}</span>
      </div>
      <p className="vstore-review-comment">{review.comment}</p>
    </div>
  );
}
